import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Card, Typography, Grid, Chip } from '@mui/material';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { fetchAdminDashboard, fetchUserDashboard } from '../../app/slices/dashboardSlice';
import { PageHeader }     from '../../components/common/PageHeader';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { StatusChip }     from '../../components/common/StatusChip';
import { formatDate }     from '../../utils/helpers';
import { STATUS_CONFIG }  from '../../utils/constants';

const KpiCard = ({ label, value, color, bg }) => (
  <Card sx={{ p: 2.5, borderTop: `3px solid ${color}`, bgcolor: bg || '#fff' }}>
    <Typography variant="h4" fontWeight={800} color={color} fontFamily="'Sora',sans-serif">{value}</Typography>
    <Typography variant="body2" color="text.secondary" mt={0.5} fontWeight={500}>{label}</Typography>
  </Card>
);

const TOOLTIP_STYLE = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' };

export default function DashboardPage() {
  const dispatch = useDispatch();
  const { user } = useSelector(s => s.auth);
  const { adminData, userData, loading } = useSelector(s => s.dashboard);
  const isAdmin = user?.role === 'Admin';

  useEffect(() => {
    if (isAdmin) dispatch(fetchAdminDashboard());
    else dispatch(fetchUserDashboard());
  }, [dispatch, isAdmin]);

  if (loading && !adminData && !userData) return <LoadingSpinner />;

  if (isAdmin && adminData) {
    const { kpi, departments, userPerformance } = adminData;
    const pieData = [
      { name: 'New',        value: kpi.new,             color: '#94a3b8' },
      { name: 'In Progress',value: kpi.inProgress,      color: '#4f46e5' },
      { name: 'On Hold',    value: kpi.onHold,          color: '#f59e0b' },
      { name: 'Completed',  value: kpi.completedByUser, color: '#10b981' },
      { name: 'Done',       value: kpi.doneByAdmin,     color: '#7c3aed' },
    ].filter(d => d.value > 0);

    return (
      <Box>
        <PageHeader title="Dashboard" subtitle="Overview of all tasks and teams" />
        <Grid container spacing={2} mb={3}>
          <Grid item xs={6} sm={4} md={2}><KpiCard label="Total"      value={kpi.total}           color="#4f46e5" bg="#eef2ff" /></Grid>
          <Grid item xs={6} sm={4} md={2}><KpiCard label="In Progress"value={kpi.inProgress}      color="#4f46e5" bg="#eef2ff" /></Grid>
          <Grid item xs={6} sm={4} md={2}><KpiCard label="On Hold"    value={kpi.onHold}          color="#d97706" bg="#fffbeb" /></Grid>
          <Grid item xs={6} sm={4} md={2}><KpiCard label="Completed"  value={kpi.completedByUser} color="#059669" bg="#ecfdf5" /></Grid>
          <Grid item xs={6} sm={4} md={2}><KpiCard label="Done"       value={kpi.doneByAdmin}     color="#7c3aed" bg="#f5f3ff" /></Grid>
          <Grid item xs={6} sm={4} md={2}><KpiCard label="Overdue"    value={kpi.overdue}         color="#dc2626" bg="#fef2f2" /></Grid>
        </Grid>

        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 2.5, height: 300 }}>
              <Typography variant="subtitle2" fontWeight={700} mb={1} fontFamily="'Sora',sans-serif">Status Distribution</Typography>
              <ResponsiveContainer width="100%" height="88%">
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={95} paddingAngle={3} dataKey="value">
                    {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Grid>
          <Grid item xs={12} md={8}>
            <Card sx={{ p: 2.5, height: 300 }}>
              <Typography variant="subtitle2" fontWeight={700} mb={1} fontFamily="'Sora',sans-serif">User Performance</Typography>
              <ResponsiveContainer width="100%" height="88%">
                <BarChart data={userPerformance.slice(0, 8)} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Bar dataKey="inProgress"      fill="#4f46e5" name="In Progress" stackId="a" radius={[0,0,0,0]} />
                  <Bar dataKey="completedByUser" fill="#10b981" name="Completed"   stackId="a" />
                  <Bar dataKey="doneByAdmin"     fill="#7c3aed" name="Done"        stackId="a" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Grid>
        </Grid>

        <Card sx={{ p: 2.5 }}>
          <Typography variant="subtitle2" fontWeight={700} mb={2} fontFamily="'Sora',sans-serif">Department Overview</Typography>
          <Grid container spacing={2}>
            {departments.map(d => (
              <Grid item xs={12} sm={6} md={4} key={d.id}>
                <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" fontWeight={700} color="text.primary">{d.name}</Typography>
                  <Box display="flex" gap={2} mt={0.75}>
                    <Typography variant="caption" color="text.secondary">{d.userCount} users</Typography>
                    <Typography variant="caption" color="text.secondary">{d.taskCount} tasks</Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Card>
      </Box>
    );
  }

  if (userData) {
    const { kpi, recentTasks } = userData;
    return (
      <Box>
        <PageHeader title="My Dashboard" subtitle={`Welcome back, ${user?.name}`} />
        <Grid container spacing={2} mb={3}>
          <Grid item xs={6} sm={4} md={2}><KpiCard label="Total"      value={kpi.total}           color="#4f46e5" bg="#eef2ff" /></Grid>
          <Grid item xs={6} sm={4} md={2}><KpiCard label="In Progress"value={kpi.inProgress}      color="#4f46e5" bg="#eef2ff" /></Grid>
          <Grid item xs={6} sm={4} md={2}><KpiCard label="On Hold"    value={kpi.onHold}          color="#d97706" bg="#fffbeb" /></Grid>
          <Grid item xs={6} sm={4} md={2}><KpiCard label="Completed"  value={kpi.completedByUser} color="#059669" bg="#ecfdf5" /></Grid>
          <Grid item xs={6} sm={4} md={2}><KpiCard label="Done"       value={kpi.doneByAdmin}     color="#7c3aed" bg="#f5f3ff" /></Grid>
          <Grid item xs={6} sm={4} md={2}><KpiCard label="Overdue"    value={kpi.overdue}         color="#dc2626" bg="#fef2f2" /></Grid>
        </Grid>
        <Card sx={{ p: 2.5 }}>
          <Typography variant="subtitle2" fontWeight={700} mb={2} fontFamily="'Sora',sans-serif">Recent Tasks</Typography>
          {(!recentTasks || recentTasks.length === 0)
            ? <Typography variant="body2" color="text.secondary">No tasks assigned yet.</Typography>
            : recentTasks.map(t => (
              <Box key={t.id} display="flex" alignItems="center" justifyContent="space-between" py={1.5}
                sx={{ borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>{t.title}</Typography>
                  <Typography variant="caption" color={t.isOverdue ? 'error.main' : 'text.secondary'}>
                    Due: {formatDate(t.dueDate)}
                  </Typography>
                </Box>
                <StatusChip status={t.status} />
              </Box>
            ))
          }
        </Card>
      </Box>
    );
  }

  return null;
}
